/**
 * @file CompanyExperiments.tsx
 * @description This file contains the CompanyExperiments component,
 * which utilizes the ExperimentTable component to work with Company Experiments data.
 */

import React from "react";
import { RouteComponentProps } from "@reach/router";
import "./ExperimentTable.scss";
import { CompanyExperiment } from "@blisspointmedia/bpm-types/dist/FeatureExperiments";
import ExperimentTable from "./ExperimentTable";
import { GroupedDataRow } from "./types";
import { OverlayTrigger } from "../Components";
import { Tooltip } from "react-bootstrap";
import * as uuid from "uuid";
import { MAX_SHOWN_GROUPED_COMPANY_NAMES, EMPTY_CELL_TEXT, formatExperimentsDate } from "./utils";

const companyColumnRenderer = (data: GroupedDataRow<CompanyExperiment, "company">) => {
  if (!data.company) {
    return <div className="experimentCellText">{EMPTY_CELL_TEXT}</div>;
  }

  // If the number of companies is less than or equal to the max, just show the names comma separated
  if (data.company.length <= MAX_SHOWN_GROUPED_COMPANY_NAMES) {
    return <div className="experimentCellText">{data.company.join(", ")}</div>;
  }

  // Otherwise, show "<companies> + <remaining> more" with an overlay on hover
  // Show all companies in the overlay
  const sortedCompanies = [...data.company].sort();
  const shownCompanies = sortedCompanies.slice(0, MAX_SHOWN_GROUPED_COMPANY_NAMES);
  const remainingCount = sortedCompanies.length - shownCompanies.length;

  return (
    <OverlayTrigger
      placement={OverlayTrigger.PLACEMENTS.BOTTOM.CENTER}
      overlay={
        <Tooltip id={uuid.v4()}>
          <div>
            {sortedCompanies.map((company, idx) => (
              <div key={idx}>{company}</div>
            ))}
          </div>
        </Tooltip>
      }
    >
      <div className="experimentCellText">
        {`${shownCompanies.join(", ")}${remainingCount > 0 ? ` + ${remainingCount} more` : ""}`}
      </div>
    </OverlayTrigger>
  );
};

const headers = [
  {
    label: "Experiment Flag",
    field: "experiment_flag",
    type: "select",
    options: "experiment_flag",
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    isInvalid: (experimentFlag: string) => {
      return !experimentFlag || experimentFlag.length === 0;
    },
  },
  {
    label: "Companies with Access",
    field: "company",
    type: "select",
    options: "company",
    isMulti: true,
    flex: 7,
    modalRow: 0,
    modalFlex: 1,
    isInvalid: (company: string) => {
      return !company || company.length === 0;
    },
    renderer: companyColumnRenderer,
  },
  {
    label: "Last Modified",
    field: "lastmodified",
    type: "text",
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    renderer: (data: CompanyExperiment) => {
      if (!data.lastmodified) {
        return <div>{EMPTY_CELL_TEXT}</div>;
      }
      return <div>{formatExperimentsDate(data.lastmodified)}</div>;
    },
    uneditable: true,
  },
  {
    label: "Modified By",
    field: "lastuser",
    type: "text",
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    uneditable: true,
  },
];

const CompanyExperiments: React.FC<RouteComponentProps> = ({ path }) => {
  // Factory function to create new CompanyExperiment rows
  const createNewCompanyExperiment = (
    groupByColumn: string,
    groupedColumn: string
  ): CompanyExperiment => {
    const now = new Date().toISOString();
    return {
      experiment_flag: groupByColumn,
      company: groupedColumn,
      created: now,
      lastmodified: now,
    };
  };

  return (
    <ExperimentTable<CompanyExperiment>
      options={{
        headers,
        name: "Company Experiments",
        addButtonText: "Add Experiment",
        createNewRow: createNewCompanyExperiment,
        groupByColumn: "experiment_flag",
        groupedColumn: "company",
        expectedKeys: ["experiment_flag", "company", "created", "lastmodified"],
        jsonField: "company_experiments",
        modalTitle: "New Company Experiment",
      }}
    />
  );
};

export default CompanyExperiments;
